"use client";
import { SearchHistoryDropdown } from "@/app/layouts/standard/header/search-bar/search-bar-dropdown/searchHistoryDropdown";
import { SearchSuggestionsDropdown } from "@/app/layouts/standard/header/search-bar/search-bar-dropdown/searchSuggestionsDropdown";
import { SearchSuggestionsResponse } from "@/fetch/algolia/getSearchSuggestions";
import { DeviceType } from "@/types/search";
import { useWindowWidth } from "@react-hook/window-size";
import delay from "delay";
import { useSearchParams } from "next/navigation";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Overlay } from "react-bootstrap";
import styles from "./searchFieldDropdown.module.scss";

export const SearchFieldDropdown = ({
  searchHistory,
  inputTarget,
  containerTarget,
  searchBarTarget,
  query,
  searchSuggestionsResponse,
  showTrigger = 0,
  dropdownItemChooseCallback,
  dropdownItemDismissCallback,
  onVisibilityChange,
}: SearchFieldDropdownProps) => {
  const [activeDropdown, setActiveDropdown] = useState<SearchDropdownType | null>(null);
  const windowWidth = useWindowWidth();
  const deviceType: DeviceType = useMemo(() => {
    if (windowWidth < 564) return "mobile";
    else if (windowWidth < 768) return "tablet";
    return "desktop";
  }, [windowWidth]);
  const params = useSearchParams();

  // Notify parent component when dropdown visibility changes
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(!!activeDropdown);
    }
  }, [activeDropdown, onVisibilityChange]);

  const responseHasContent = useMemo(() => {
    return !!searchSuggestionsResponse && searchSuggestionsResponse.textSearchSuggestions?.length > 0;
  }, [searchSuggestionsResponse]);

  useEffect(() => {
    if (responseHasContent) {
      setActiveDropdown(SearchDropdownType.SEARCH_SUGGESTIONS);
    }
  }, [responseHasContent]);

  const reportClosedCallback = (dropdownType: SearchDropdownType) => {
    if (activeDropdown === dropdownType) {
      setActiveDropdown(null);
    }
  };

  useEffect(() => {
    if (searchHistory?.length === 0) {
      if (activeDropdown === SearchDropdownType.LOCAL_HISTORY) {
        setActiveDropdown(null);
      }
    }
  }, [activeDropdown, searchHistory]);

  // Close search suggestions when a new search has completed.
  useEffect(() => {
    setActiveDropdown(null);
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    const delayedShow = async () => {
      if (showTrigger > 0) {
        await delay(250);
        if (cancelled) return;
        // Recording a search re-runs this effect via searchHistory, and submitting blurs the
        // field -- without this the panel would re-open 250ms after the user pressed enter.
        const inputElement = inputTarget.current;
        if (!inputElement || document.activeElement !== inputElement) return;
        const inputValue = inputElement.value;
        if (!inputValue?.length && searchHistory && searchHistory.length > 0) {
          setActiveDropdown(SearchDropdownType.LOCAL_HISTORY);
        } else if (inputValue?.length && responseHasContent) {
          setActiveDropdown(SearchDropdownType.SEARCH_SUGGESTIONS);
        }
      }
    };
    delayedShow();
    return () => {
      cancelled = true;
    };
  }, [showTrigger, searchHistory, responseHasContent]);

  useEffect(() => {
    if (responseHasContent) {
      setActiveDropdown(SearchDropdownType.SEARCH_SUGGESTIONS);
    }
  }, [responseHasContent, searchSuggestionsResponse]);

  function handleClose(): void {
    setActiveDropdown(null);
  }
  useLayoutEffect(() => {
    if (activeDropdown === SearchDropdownType.LOCAL_HISTORY && inputTarget.current?.value?.length) {
      setActiveDropdown(null);
    }
  });

  const conditionsMetForAutoCompleteShow = useMemo(() => {
    if (activeDropdown === SearchDropdownType.SEARCH_SUGGESTIONS) {
      if (!responseHasContent || !query || query.length === 0) {
        setActiveDropdown(query?.length ? null : SearchDropdownType.LOCAL_HISTORY);
        return false;
      }
    }

    if (query && query.length > 0 && searchSuggestionsResponse?.inputQuery === query && responseHasContent) {
      return true;
    }
    return false;
  }, [activeDropdown, query, responseHasContent, searchSuggestionsResponse?.inputQuery]);

  return (
    <Overlay
      show={!!activeDropdown}
      target={deviceType === "mobile" || deviceType === "tablet" ? searchBarTarget.current : inputTarget.current}
      container={deviceType === "mobile" || deviceType === "tablet" ? searchBarTarget.current : containerTarget.current}
      placement="bottom"
      rootClose
      onHide={() => handleClose()}
    >
      <div className={styles["searchSuggestionsContainer"]}>
        {activeDropdown === SearchDropdownType.LOCAL_HISTORY && searchHistory && searchHistory.length > 0 && (
          <SearchHistoryDropdown
            searchHistory={searchHistory}
            dropdownItemChooseCallback={(searchItem) => {
              handleClose();
              dropdownItemChooseCallback(searchItem);
            }}
            dropdownItemDismissCallback={dropdownItemDismissCallback}
            reportClosedCallback={reportClosedCallback}
          />
        )}
        {activeDropdown === SearchDropdownType.SEARCH_SUGGESTIONS && conditionsMetForAutoCompleteShow && (
          <SearchSuggestionsDropdown
            searchSuggestionsResponse={searchSuggestionsResponse}
            dropdownItemChooseCallback={(searchItem) => {
              handleClose();
              dropdownItemChooseCallback(searchItem);
            }}
            reportClosedCallback={reportClosedCallback}
          />
        )}
      </div>
    </Overlay>
  );
};

type SearchFieldDropdownProps = {
  searchHistory: string[] | undefined | null;
  inputTarget: React.RefObject<HTMLInputElement | null>;
  containerTarget: React.RefObject<HTMLDivElement | null>;
  searchBarTarget: React.RefObject<HTMLFormElement | null>;
  searchFieldFocusEvent: SearchFieldFocusEvent | null;
  query: string | undefined;
  searchSuggestionsResponse: SearchSuggestionsResponse | undefined | null;
  dropdownItemChooseCallback: (elementDetails: SearchFieldElement) => void;
  dropdownItemDismissCallback?: (elementDetails: SearchFieldElement) => void;
  showTrigger: number;
  onVisibilityChange?: (isVisible: boolean) => void;
};

export enum SearchDropdownType {
  LOCAL_HISTORY = "LOCAL_HISTORY",
  SEARCH_SUGGESTIONS = "SEARCH_SUGGESTIONS",
}

export type SearchFieldElement = {
  dropdownType: SearchDropdownType;
  label: string;
  prefix?: string;
  unboldedQuery?: string;
  suffix?: string;
  id: string;
};

export type SearchFieldFocusEvent = {
  displayText: string | null;
  nowFocused: boolean;
};
